import type { TimeRange } from '@mimir/common/util/timeRange';
import type Timeline from '@mimir/client-common/timeline/Timeline';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { SearchResultHit, Item } from '@mimir/common/dto/searchResultHit';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import type { SubclipParams } from '@mimir/common/dto/itemPropTypes';
import type { StartAnalyticsJobRequestDto } from '@mimir/common/dto/itemModels';
import type { DynamoItemType} from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { AnalyticConfig } from '@mimir/common/dto/tenantModel';
import type { CreateVirtualClipPayload } from '@mimir/common/dto/virtualClipUtil';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import { isInstanceOfVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import type { Dispatch } from 'vuex';
import { normalizeAnalyticConfigForItem } from '../model/upload-util';
import { createVirtualClip, startBackendJob, updateClipList } from './api';
import type { ClipListUpdateDto } from '@mimir/common/dto/clipListDto';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { ItemMediaPlayerOps } from '../model/itemMediaPlayerOps';

export const doCreateItem = async (
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  dispatch: Dispatch,
  folderId: string | undefined,
  metadata: ItemMetaData,
  analyticConfig: AnalyticConfig,
  visibleTo: ItemAccessControl | undefined,
  mouseEvent: MouseEvent | undefined,
  type: DynamoItemType | undefined,
  shouldInject: boolean,
  itemIdToCopyMetadataFrom: string | undefined
) => {
  /**
   * User's shortcut to create a clean copy: holds `alt + ctrl (or command)` + click-on-create-item.
   */
  const doCreateCleanCopy = mouseEvent && ((mouseEvent.altKey && mouseEvent.ctrlKey) || mouseEvent.metaKey);
  const placeholder: Partial<ItemCreationRequestDto> = {
    metadata: metadata,
    itemType: type ?? ItemType.FILE,
    folderParents: folderId ? [folderId] : undefined,
    itemIdToCopyMetadataFrom,
    visibleTo: visibleTo,
    executeSameActionOnArchiveCopy: doCreateCleanCopy,
    ...normalizeAnalyticConfigForItem(analyticConfig),
  };

  const hit = await dispatch('createPlaceHolderAndSearchHit', {
    applicationContext,
    item: placeholder,
    inject: shouldInject,
    isProxyMode,
  }) as SearchResultHit;
  return hit;
};

export async function createNewVirtualClip(
  timeline: Timeline,
  playerOps: ItemMediaPlayerOps,
  item: Pick<Item, 'id' | 'virtualClipInfo'>,
  folderId: string | undefined,
  metadata: ItemMetaData,
  visibleTo: ItemAccessControl | undefined
) {
  const masterClipId = isInstanceOfVirtualClip(item.virtualClipInfo)
    ? item.virtualClipInfo.masterClipId
    : item.id;

  const { startOffset, inPoint, outPoint } = getVirtualClipMarkers(timeline, playerOps, item);
  const virtualClipInfo: VirtualClipModel | CreateVirtualClipPayload = {
    metadata,
    masterClipId,
    parentClipId: item.id === masterClipId ? undefined : item.id,
    startOffsetMasterClip: startOffset,
    inPoint,
    outPoint,
    folderParents: folderId ? [folderId] : undefined,
    visibleTo: visibleTo,
  };
  await createVirtualClip(virtualClipInfo);
}

export function getVirtualClipMarkers(
  timeline: Timeline,
  playerOps: ItemMediaPlayerOps,
  item: Pick<Item, 'virtualClipInfo'>
) {
  const startOffset = item.virtualClipInfo?.inPoint ?? 0;
  const virtualClipStart = playerOps.getVirtualClipStartObs().currentValue();
  const durationInMs = playerOps.getDurationInMsObs().currentValue();
  return {
    startOffset,
    // TODO: This does not make sense? First we inject virtualClipStart to
    // getMarkIn function in order to get the mark in relative to the
    // virtualClipStart and then we add this offset. Is there any point in
    // this?
    inPoint: timeline.getMarkIn(virtualClipStart) + startOffset,
    outPoint: timeline.getMarkOut(durationInMs, virtualClipStart) + startOffset
  };
}

export const getSubClipMarkers = (item: Pick<Item, 'virtualClipInfo'>, marks: TimeRange) => {
  const startTimeOffset = isInstanceOfVirtualClip(item.virtualClipInfo) ? item.virtualClipInfo.inPoint : 0;
  return {
    startTime: marks.start + startTimeOffset,
    endTime: marks.end + startTimeOffset,
  };
};

export const createSubClip = async (currentItem: Item, subclipParams: SubclipParams)  => {
  const payload: StartAnalyticsJobRequestDto = {
    itemId : isInstanceOfVirtualClip(currentItem.virtualClipInfo) ? currentItem.virtualClipInfo.masterClipId : currentItem.id,
    operationType: 'subclip',
    operationParams: subclipParams,
  };

  return await startBackendJob(payload);
};

export const doCreateClipListItem = async (
  isProxyMode: boolean,
  applicationContext: ApplicationContext,
  dispatch: Dispatch,
  folderId: string | undefined,
  metadata: ItemMetaData,
  analyticConfig: AnalyticConfig,
  visibleTo: ItemAccessControl | undefined,
  clipList: ClipListUpdateDto,
) => {
  const item = await doCreateItem(isProxyMode, applicationContext, dispatch, folderId, metadata, analyticConfig, visibleTo, new MouseEvent('click'), ItemType.CLIP_LIST, true, undefined);
  await updateClipList(item.id, clipList, 0);
  return item.id;
};
